/**
 * 설문 문항 편집 — **순서 바꾸기**와 화면 전용 키. (2026-10-12)
 *
 * 순서를 바꿔도 기존 응답은 그대로다: 응답은 문항 **제목**을 키로 저장되고(`survey.js`), 분석 탭은 이 배열 순서대로 문항을 보인다.
 * 제목을 바꾸면 과거 응답과 끊기는 것은 순서와 무관한 별개 문제다.
 *
 * 화면 전용 키(`_k`)가 필요한 이유: 문항 목록을 인덱스로만 그리면, 순서를 바꿨을 때 처음 값만 받는 입력(문항 유형 RadioGroup)이
 * **옮기기 전 자리의 값을 그대로 보여준다.** 문항마다 고정 키를 주고, **저장할 때는 떼어** 설문 JSON 에 들어가지 않게 한다.
 */

let seq = 0;

/** 문항마다 화면 전용 키를 붙인다(이미 있으면 유지). */
export const withKey = (question) => (question?._k ? question : {...question, _k: `q${++seq}`});

export const withKeys = (questions) => (questions ?? []).map(withKey);

/** index 의 문항을 delta(-1 위 / +1 아래)만큼 옮긴 새 배열. 범위를 벗어나면 그대로. */
export const moveQuestion = (questions, index, delta) => {
    const to = index + delta;
    if (index < 0 || index >= questions.length || to < 0 || to >= questions.length) return questions;
    const next = [...questions];
    [next[index], next[to]] = [next[to], next[index]];
    return next;
};

/** 저장용 — 화면 전용 키를 뗀다. */
export const stripKeys = (questions) => (questions ?? []).map(({_k, ...question}) => question);

/**
 * 문항 유형(풀다운). 유형이 늘면 여기에만 추가한다 — 단, 값은 redirect-service `Project.ProjectSettings.Survey.Question.Type`
 * enum 과 같아야 한다(없는 값을 저장하면 서버가 거부한다). 기본값 = 객관식(2026-10-23, 종전 주관식).
 */
export const QUESTION_TYPES = [
    {value: "MULTIPLE_CHOICE", label: "객관식"},
    {value: "SHORT_ANSWER", label: "주관식"},
    {value: "SCALE_5", label: "5점 척도"},
];
export const DEFAULT_QUESTION_TYPE = "MULTIPLE_CHOICE";

/**
 * 5점 척도의 선택지 — 이 **순서 그대로** 고객 화면에 나온다(5 → 1). 편집 화면에서는 고칠 수 없다.
 * 설문 JSON 에는 `values` 로 그대로 저장되므로 고객 화면(`survey.js`)·분석 탭은 객관식과 같은 경로로 다룬다.
 */
export const SCALE_5_VALUES = [
    "5 - 매우 그렇다",
    "4 - 그렇다",
    "3 - 보통이다",
    "2 - 그렇지 않다",
    "1 - 전혀 그렇지 않다",
];

/** '+ 질문 추가하기' 로 생기는 문항 — 객관식 · 필수 (2026-10-23). 제목은 비워 둔다(placeholder 안내). */
export const newQuestion = () => withKey({type: DEFAULT_QUESTION_TYPE, required: true, etc: false, values: []});

/**
 * 유형 풀다운 변경. 5점 척도로 바꾸면 선택지를 고정 5개로 채우고 '기타' 를 끈다.
 * 5점 척도에서 다른 유형으로 돌아가면 고정 선택지는 비운다(척도 문구가 객관식 선택지로 남지 않게).
 */
export const changeQuestionType = (question, type) => {
    if (type === "SCALE_5") return {...question, type, values: [...SCALE_5_VALUES], etc: false};
    if (question.type === "SCALE_5") return {...question, type, values: []};
    return {...question, type};
};

/**
 * 여러 줄 텍스트를 선택지 목록으로 쪼갠다 — 선택지 입력칸에 **여러 줄을 붙여넣을 때** 쓴다(2026-10-23).
 * AI 가 만들어 준 설문의 선택지를 통째로 복사해 오면 줄머리의 번호·글머리표('1.', '1)', '①', '-', '•')는 떼고 빈 줄은 버린다.
 * 한 줄뿐이면 그 줄 하나다(호출부는 이때 기본 붙여넣기를 그대로 둔다).
 * ⚠️ '5 - 매우 그렇다' 처럼 숫자 뒤에 점·괄호가 **없는** 줄은 번호로 보지 않는다(척도 문구를 깎지 않게).
 */
const LINE_MARKER = /^\s*(?:[-–—•·▪◦*]|\(?\d{1,2}[.)]|[①②③④⑤⑥⑦⑧⑨⑩]|[a-zA-Z][.)])\s*/;
export const splitPastedLines = (text) =>
    (text ?? "")
        .split(/\r?\n/)
        .map(line => line.replace(LINE_MARKER, "").trim())
        .filter(line => line.length > 0);

/**
 * 고객 설문 화면 미리보기(`/view/survey?temp=true`)로 보내는 메시지. 화면 전용 키는 떼고 보낸다.
 * 메시지 이름은 redirect-service `static/front/assets/survey.js` 의 `PREVIEW_MESSAGE` 와 같아야 한다.
 */
export const SURVEY_PREVIEW_MESSAGE = "OMOPLAY_SURVEY_PREVIEW";
export const toPreviewMessage = (title, description, questions) => ({
    type: SURVEY_PREVIEW_MESSAGE,
    survey: {title, description, questions: stripKeys(questions)},
});

/**
 * 자동 순번 '1.'(2026-10-23, 종전 '(1)'). 고객 화면·편집 화면·분석 탭이 같은 표기를 쓴다 — 고객 화면은 `survey.js` 에 따로 있다.
 * 순번은 **제목과 따로** 붙는다 — 응답 키는 제목이라, 제목에 번호를 쓰면 순서를 바꿀 때 번호를 고치는 순간 다른 문항이 된다.
 */
export const questionNumberLabel = (index) => `${index + 1}.`;

/**
 * 제목 앞에 손으로 쓴 번호('Q1.', 'Q1 ', '1.', '1)', '1번', '(1)')가 있으면 그 부분, 없으면 null.
 * 편집 화면 경고용이다 — **자동으로 지우지 않는다**(지우면 제목=키가 바뀌어 과거 응답과 끊긴다).
 * 연도·수량('2026년 방문', '3개월') 은 걸리지 않게 번호는 1~2자리 + 구분자로 한정한다.
 */
const MANUAL_NUMBER = /^\s*(?:\(\s*\d{1,2}\s*\)|\[\s*\d{1,2}\s*\]|[Qq]\s*\d{1,2}\s*[.):-]?|\d{1,2}\s*[.):번]|\d{1,2}\s*-\s)\s*/;
export const manualNumberPrefix = (title) => {
    if (!title) return null;
    const match = title.match(MANUAL_NUMBER);
    if (!match) return null;
    // 번호만 있고 내용이 없으면(입력 중) 경고하지 않는다
    return title.slice(match[0].length).trim().length > 0 ? match[0].trim() : null;
};

/**
 * 분석 탭용 — 응답 시트의 문항 목록(`{title, current}`, 정의 순서)에서 **현재 문항의 제목 → '1.'**.
 * 정의에 없는 이전 문항(current=false)에는 번호를 주지 않는다(고객 화면에 더는 나오지 않는다).
 */
export const currentQuestionNumbers = (sheetQuestions) => {
    const numbers = new Map();
    (sheetQuestions ?? []).filter(q => q.current).forEach((q, i) => numbers.set(q.title, questionNumberLabel(i)));
    return numbers;
};
